import type { PlaceFromWebsiteResponse } from '@pulsify/shared/dto'
import { useQueryClient } from '@tanstack/react-query'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { type ReactElement, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { listingImagesApi } from '~/entities/listing/api'
import { useCreateListing, usePublishListing } from '~/entities/listing/model/use-listings'
import { useReadPlaceWebsite } from '~/entities/listing/model/use-place-website'
import { GuardedActionButton } from '~/features/shared/components/guarded-action-button'
import * as m from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime.js'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { log } from '~/shared/lib/logger'
import { EASING } from '~/shared/lib/motion'
import { QueryKey, queryKey } from '~/shared/lib/query-keys'
import { Button } from '~/shared/ui/button'
import {
  type DraftProblem,
  draftFromWebsite,
  draftProblem,
  orderedPhotos,
  type PhotoPick,
  type PlaceDraft,
  photoFile,
  photoFromFile,
  toCreateListingInput,
} from './map-place'
import { ReviewStep } from './review-step'
import { UrlStep } from './url-step'
import { WorkingStep } from './working-step'

type Stage = 'url' | 'working' | 'review'
type Phase = 'idle' | 'creating' | 'uploading' | 'publishing'

const PROBLEM_MESSAGE: Record<DraftProblem, () => string> = {
  name: m.placeWebNeedName,
  address: m.placeWebNeedAddress,
  category: m.placeWebNeedCategory,
  description: m.placeWebNeedDescription,
}

/** How saving a new place ended: kept as a draft, published, or saved but not published. */
export type PlaceSaveOutcome = 'saved' | 'published' | 'publishFailed'

interface PlaceWebsiteFlowProps {
  /** The link to the manual form, shown under the website field. */
  manualLink: ReactElement
  onSaved: (listingId: string, outcome: PlaceSaveOutcome) => void
}

export function PlaceWebsiteFlow({ manualLink, onSaved }: PlaceWebsiteFlowProps) {
  const queryClient = useQueryClient()
  const reduceMotion = useReducedMotion()
  const { data: business } = useCurrentBusiness()
  const businessId = business?.id ?? ''
  const readWebsite = useReadPlaceWebsite(businessId)
  const createListing = useCreateListing(businessId)
  const publishListing = usePublishListing(businessId)

  const [stage, setStage] = useState<Stage>('url')
  const [url, setUrl] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [draft, setDraft] = useState<PlaceDraft | null>(null)
  const [photos, setPhotos] = useState<PhotoPick[]>([])
  const [coverUrl, setCoverUrl] = useState<string | null>(null)
  const [phase, setPhase] = useState<Phase>('idle')
  const [publishing, setPublishing] = useState(false)
  const requestRef = useRef(0)
  const createdIdRef = useRef<string | null>(null)

  const enterReview = (place: PlaceFromWebsiteResponse) => {
    setDraft(draftFromWebsite(place))
    setPhotos(place.photos.map((photo) => ({ photo, selected: true })))
    setCoverUrl(place.photos[0]?.sourceUrl ?? null)
    setStage('review')
  }

  const start = async (nextUrl: string) => {
    if (!businessId) return
    const request = ++requestRef.current
    setUrl(nextUrl)
    setError(null)
    setStage('working')
    try {
      const place = await readWebsite.mutateAsync({
        url: nextUrl,
        locale: getLocale() === 'en' ? 'en' : 'de',
      })
      if (request === requestRef.current) enterReview(place)
    } catch (readError) {
      if (request !== requestRef.current) return
      setError(getErrorMessageFromError(readError))
      setStage('url')
    }
  }

  const startOver = () => {
    requestRef.current++
    createdIdRef.current = null
    setDraft(null)
    setPhotos([])
    setCoverUrl(null)
    setStage('url')
  }

  const togglePhoto = (sourceUrl: string) => {
    const next = photos.map((pick) =>
      pick.photo.sourceUrl === sourceUrl ? { ...pick, selected: !pick.selected } : pick,
    )
    setPhotos(next)
    const selected = next.filter((pick) => pick.selected)
    if (!selected.some((pick) => pick.photo.sourceUrl === coverUrl)) {
      setCoverUrl(selected[0]?.photo.sourceUrl ?? null)
    }
  }

  const adjustCover = async (file: File) => {
    if (!coverUrl) return
    const photo = await photoFromFile(file, coverUrl)
    setPhotos((current) =>
      current.map((pick) => (pick.photo.sourceUrl === coverUrl ? { ...pick, photo } : pick)),
    )
  }

  const addOwnPhoto = async (file: File) => {
    const photo = await photoFromFile(file, `own:${crypto.randomUUID()}`)
    setPhotos((current) => [{ photo, selected: true }, ...current])
    setCoverUrl(photo.sourceUrl)
  }

  const uploadPhotos = async (listingId: string) => {
    const files = orderedPhotos(photos, coverUrl).map((photo, index) => photoFile(photo, index))
    let uploaded = 0
    for (const file of files) {
      try {
        await listingImagesApi.upload(listingId, file)
        uploaded++
      } catch (uploadError) {
        log.error(
          uploadError instanceof Error ? uploadError : new Error(String(uploadError)),
          'Website photo upload failed',
        )
      }
    }
    if (uploaded < files.length) {
      toast.warning(m.placeWebPhotosPartial(), {
        description: m.placeWebPhotosPartialDescription(),
      })
    }
  }

  const save = async (publish: boolean) => {
    if (!draft || !businessId || phase !== 'idle') return
    const problem = draftProblem(draft)
    if (problem) {
      toast.error(PROBLEM_MESSAGE[problem]())
      return
    }
    setPublishing(publish)
    const name = draft.name.trim()
    try {
      let listingId = createdIdRef.current

      if (!listingId) {
        setPhase('creating')
        const created = await createListing.mutateAsync(toCreateListingInput(draft))
        listingId = created.id
        createdIdRef.current = listingId
        setPhase('uploading')
        await uploadPhotos(listingId)
        await queryClient.invalidateQueries({ queryKey: queryKey(QueryKey.LISTINGS, businessId) })
      }
      if (publish) {
        setPhase('publishing')
        try {
          await publishListing.mutateAsync(listingId)
          toast.success(m.placeWebPublished({ name }))
        } catch (publishError) {
          toast.error(m.failedToPublishListing(), {
            description: getErrorMessageFromError(publishError),
          })
          onSaved(listingId, 'publishFailed')
          return
        }
        onSaved(listingId, 'published')
      } else {
        toast.success(m.placeWebCreated({ name }))
        onSaved(listingId, 'saved')
      }
    } catch (saveError) {
      log.error(
        saveError instanceof Error ? saveError : new Error(String(saveError)),
        'Save failed',
      )
      toast.error(getErrorMessageFromError(saveError))
    } finally {
      setPhase('idle')
    }
  }

  const busy = phase !== 'idle'
  const busyLabel =
    phase === 'creating'
      ? m.placeWebPhaseCreating()
      : phase === 'uploading'
        ? m.placeWebPhaseUploading({ count: orderedPhotos(photos, coverUrl).length })
        : m.placeWebPhasePublishing()
  const hasCover = orderedPhotos(photos, coverUrl).length > 0

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={stage}
        initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -8 }}
        transition={reduceMotion ? { duration: 0.15 } : { duration: 0.28, ease: EASING }}
      >
        {stage === 'url' && (
          <UrlStep initialUrl={url} error={error} onSubmit={start} manualLink={manualLink} />
        )}
        {stage === 'working' && <WorkingStep url={url} />}
        {stage === 'review' && draft && (
          <div className="mx-auto w-full max-w-5xl pb-4">
            <ReviewStep
              draft={draft}
              onDraftChange={(patch) => setDraft((current) => current && { ...current, ...patch })}
              photos={photos}
              coverUrl={coverUrl}
              onTogglePhoto={togglePhoto}
              onCover={setCoverUrl}
              onAdjustCover={adjustCover}
              onAddPhoto={addOwnPhoto}
            />
            <div className="squircle sticky bottom-4 z-10 mt-6 flex flex-wrap items-center gap-2 rounded-xl border bg-card/90 p-2.5 shadow-[0_12px_32px_-16px_rgb(10_10_11/0.35)] backdrop-blur-md">
              <Button
                type="button"
                variant="ghost"
                className="text-muted-foreground"
                disabled={busy}
                onClick={startOver}
              >
                {m.placeWebStartOver()}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="ml-auto"
                disabled={busy || !businessId}
                onClick={() => save(false)}
              >
                {busy && !publishing ? busyLabel : m.placeWebSaveDraft()}
              </Button>
              <GuardedActionButton
                enabled={hasCover}
                hint={m.addACoverImageToPublish()}
                label={m.placeWebPublish()}
                busyLabel={busyLabel}
                busy={busy && publishing}
                disabled={busy || !businessId}
                variant="brand"
                onClick={() => save(true)}
              />
            </div>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  )
}
